import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useContent } from '../../contexts/ContentContext';
import { useTranslation } from '../../i18n';
import useWeddingSlug from '../../hooks/useWeddingSlug';
import BottomFloatingMenu from '../../components/ui/BottomFloatingMenu';
import Icon from '../../components/AppIcon';

const WeddingInfo = () => {
  const navigate = useNavigate();
  const { weddingPath } = useWeddingSlug();
  const { content } = useContent();
  const { t } = useTranslation();
  const extraInfo = content?.extraInfo;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!extraInfo?.enabled) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card rounded-xl p-6 md:p-8 shadow-romantic-md border border-border text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <Icon name="Info" size={32} color="var(--color-muted-foreground)" />
          </div>
          <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
            {t('info.notAvailable')}
          </h2>
          <p className="text-sm md:text-base text-muted-foreground mb-6">
            {t('info.notAvailableDesc')}
          </p>
          <button
            onClick={() => navigate(weddingPath())}
            className="text-primary hover:text-primary/80 font-medium transition-romantic"
          >
            {t('info.returnHome')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{extraInfo?.title || t('info.title')} - {content?.couple?.names || 'Wedding Invitation'}</title>
        <meta name="description" content="Additional information about our wedding celebration" />
      </Helmet>

      <div className="min-h-screen bg-background pb-24">
        <header className="bg-card border-b border-border shadow-romantic-sm sticky top-0 z-50">
          <div className="max-w-screen-xl mx-auto px-4 md:px-6 lg:px-8 py-4 md:py-5">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-romantic press-scale"
                aria-label="Go back"
              >
                <Icon name="ArrowLeft" size={20} />
                <span className="text-sm md:text-base font-medium">{t('nav.back')}</span>
              </button>
              <h1 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
                {extraInfo?.title || 'Information'}
              </h1>
              <div className="w-16" />
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
          <div className="mb-8 text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Icon name="FileText" size={32} color="var(--color-primary)" />
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-3">
              {extraInfo?.title || t('info.title')}
            </h2>
            <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto">
              {t('info.subtitle')}
            </p>
          </div>

          <div className="space-y-6">
            {extraInfo?.sections?.map((section, index) => (
              <div
                key={section.id || index}
                className="bg-card rounded-xl p-5 md:p-6 shadow-romantic-sm border border-border"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon name={section.icon || 'Info'} size={24} color="var(--color-primary)" />
                    </div>
                  </div>

                  <div className="flex-1">
                    <h3 className="text-lg md:text-xl font-semibold text-foreground mb-3">
                      {section.heading}
                    </h3>

                    {section.content && (
                      <div className="text-sm md:text-base text-muted-foreground whitespace-pre-line">
                        {section.content}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {(!extraInfo?.sections || extraInfo.sections.length === 0) && (
              <div className="text-center py-12">
                <Icon name="FileText" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                <p className="text-muted-foreground">{t('info.noItems')}</p>
              </div>
            )}
          </div>
        </main>

        <BottomFloatingMenu />
      </div>
    </>
  );
};

export default WeddingInfo;
